/**
 * ViewportMediaSection
 *
 * 延迟加载媒体资源，直到模块接近视口时再预加载内部全部媒体
 * （包括 tab / accordion 中当前不可见的面板），并根据模块可见性
 * 控制 autoplay 视频的播放与暂停。
 */
class ViewportMediaSection extends HTMLElement {
  connectedCallback() {
    if (this._observer) return;

    this._preloaded = false;
    this._isVisible = false;

    // 可通过 root-margin 属性自定义预加载触发距离
    const rootMargin = this.getAttribute('root-margin') || '600px 0px 600px 0px';

    this._observer = new IntersectionObserver(
      (entries) => this._onIntersect(entries[0]),
      { rootMargin, threshold: 0 }
    );

    this._observer.observe(this);
  }

  disconnectedCallback() {
    this._observer?.disconnect();
    this._observer = null;
  }

  /** 模块是否处于视口内 */
  get isVisible() {
    return this._isVisible;
  }

  /** 模块内媒体是否已完成预加载 */
  get isPreloaded() {
    return this._preloaded;
  }

  /** 暂停模块内所有视频（供外部组件调用） */
  pauseAllVideos() {
    this.querySelectorAll('video').forEach((video) => {
      if (!video.paused) {
        video.pause();
      }
    });
  }

  _onIntersect(entry) {
    if (!entry) return;

    if (entry.isIntersecting) {
      // 接近或进入视口：先预加载，再通知子组件可播放
      this._preload();

      if (!this._isVisible) {
        this._isVisible = true;
        this.dispatchEvent(new CustomEvent('viewport-media:visible', { bubbles: false }));
        this._playEligibleVideos();
      }
    } else if (this._isVisible) {
      // 离开视口：暂停视频并通知子组件
      this._isVisible = false;
      this.pauseAllVideos();
      this.dispatchEvent(new CustomEvent('viewport-media:hidden', { bubbles: false }));
    }
  }

  _preload() {
    if (this._preloaded) return;
    this._preloaded = true;

    // 图片：取消 lazy，兼容 data-src 懒加载写法
    this.querySelectorAll('img[loading="lazy"]').forEach((img) => {
      img.loading = 'eager';

      if (img.dataset.src && !img.src) {
        img.src = img.dataset.src;
        img.removeAttribute('data-src');
      }
    });

    // 视频：激活 source / src 并开始缓冲
    this.querySelectorAll('video').forEach((video) => {
      video.querySelectorAll('source[data-src]').forEach((source) => {
        if (source.dataset.src) {
          source.src = source.dataset.src;
          source.removeAttribute('data-src');
        }
      });

      if (video.dataset.src && !video.src) {
        video.src = video.dataset.src;
        video.removeAttribute('data-src');
      }

      if (video.preload === 'none') {
        video.preload = 'auto';
      }

      video.load();
    });

    this.dispatchEvent(new CustomEvent('viewport-media:preloaded', { bubbles: false }));
  }

  /** 播放视口内可见且允许 autoplay 的视频 */
  _playEligibleVideos() {
    this.querySelectorAll('video').forEach((video) => {
      if (!video.autoplay && !video.hasAttribute('autoplay')) return;
      // 跳过 aria-hidden 面板内的视频（如未激活的 tab）
      if (this._isInHiddenAncestor(video)) return;

      video.muted = true;
      video.playsInline = true;

      const playPromise = video.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {});
      }
    });
  }

  /** 判断视频是否位于 aria-hidden="true" 的祖先节点内 */
  _isInHiddenAncestor(video) {
    let node = video.parentElement;

    while (node && node !== this) {
      if (node.getAttribute('aria-hidden') === 'true') {
        return true;
      }

      node = node.parentElement;
    }

    return false;
  }
}

if (!window.customElements.get('viewport-media-section')) {
  window.customElements.define('viewport-media-section', ViewportMediaSection);
}
